import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/refund")({
  component: RefundPage,
  head: () => ({
    meta: [
      { title: "Refund Policy | Spirit Science Summit" },
      {
        name: "description",
        content:
          "Refund Policy for the Spirit Science Summit on September 12, 2026 in Davenport, Florida.",
      },
    ],
  }),
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-8">
      <h2 className="text-xl font-semibold text-foreground">{title}</h2>
      <div className="mt-3 space-y-3 text-muted-foreground leading-relaxed">{children}</div>
    </section>
  );
}

function RefundPage() {
  return (
    <main className="min-h-screen bg-background">
      <div className="mx-auto max-w-3xl px-6 py-16">
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          ← Back to home
        </Link>

        <h1 className="mt-6 text-4xl font-bold text-foreground">Refund Policy</h1>

        <div className="mt-6 space-y-1 text-sm text-muted-foreground">
          <p><span className="text-foreground font-medium">Effective Date:</span> May 21, 2026</p>
          <p><span className="text-foreground font-medium">Event Date:</span> September 12, 2026</p>
          <p>
            <span className="text-foreground font-medium">Contact:</span>{" "}
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
        </div>

        <Section title="1. General Refund Policy">
          <p>
            We understand that plans can change. Because this is a limited-capacity event with
            advance planning, catering, venue, translation, and speaker-related expenses, refunds
            are subject to the terms below.
          </p>
        </Section>

        <Section title="2. Refund Deadline">
          <p>
            Refund requests must be submitted in writing to info@spiritsciencesummit.com no later
            than:
          </p>
          <p className="text-foreground font-medium">
            September 9th, 2026, at 11:59 PM Eastern Time
          </p>
          <p>
            Refund requests received on or before this deadline may be eligible for a refund, minus
            any non-refundable processing fees charged by the ticketing or payment platform.
          </p>
        </Section>

        <Section title="3. No Refunds After Deadline">
          <p>
            Refund requests submitted after September 9th, 2026 will not be eligible for a refund,
            except in the case of event cancellation as described below.
          </p>
          <p>
            This policy allows us to properly manage venue capacity, food orders, translation
            services, printed materials, and event logistics.
          </p>
        </Section>

        <Section title="4. Ticket Transfers">
          <p>
            If you cannot attend and the refund deadline has passed, you may request to transfer
            your ticket to another person.
          </p>
          <p>
            Transfer requests must be submitted by email to info@spiritsciencesummit.com no later
            than September 9th, 2026.
          </p>
          <p>Please include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your full name</li>
            <li>Your email address used for purchase</li>
            <li>The new attendee's full name</li>
            <li>The new attendee's email address</li>
          </ul>
          <p>Transfers are not guaranteed until confirmed by the event organizers.</p>
        </Section>

        <Section title="5. Event Postponement">
          <p>
            If the event is postponed, your ticket will automatically remain valid for the new
            event date.
          </p>
          <p>
            If you are unable to attend the rescheduled date, refund options may be provided
            depending on the circumstances and timing of the postponement.
          </p>
        </Section>

        <Section title="6. Event Cancellation">
          <p>
            If the event is canceled and not rescheduled, ticket holders will be eligible for a
            refund of the ticket price paid.
          </p>
          <p>
            Processing fees charged by third-party ticketing or payment providers may be
            non-refundable.
          </p>
        </Section>

        <Section title="7. Speaker, Schedule, or Program Changes">
          <p>
            Changes to the event agenda, speaker lineup, session order, translation logistics,
            lunch menu, or schedule do not qualify for a refund.
          </p>
          <p>We reserve the right to make reasonable changes to the program when necessary.</p>
        </Section>

        <Section title="8. Travel and Personal Expenses">
          <p>
            The organizers are not responsible for travel, lodging, transportation, parking,
            childcare, lost wages, or any other personal expenses related to attending the event.
          </p>
          <p>Refunds apply only to eligible ticket purchases.</p>
        </Section>

        <Section title="9. How to Request a Refund">
          <p>To request a refund, email:</p>
          <p>
            <a href="mailto:info@spiritsciencesummit.com" className="underline hover:text-foreground">
              info@spiritsciencesummit.com
            </a>
          </p>
          <p>Please include:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Your full name</li>
            <li>Email address used for purchase</li>
            <li>Ticket confirmation number, if available</li>
            <li>Reason for the refund request</li>
          </ul>
          <p>
            Approved refunds will be processed back to the original payment method when possible.
            Processing times may vary depending on the payment provider or financial institution.
          </p>
        </Section>
      </div>
    </main>
  );
}
